import { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Empty,
  Button,
  Space,
  Form,
  Input,
  Select,
  DatePicker,
  Typography,
  Grid,
  Spin,
  Table,
  Tooltip,
  Pagination,
} from 'antd'
import { PlusCircleOutlined, ReloadOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import TaskCard from '../components/Task/TaskCard'
import { queryTasks, resetTaskList } from '../store/slices/taskSlice'
import { formatDate, formatDateTime, isSubscriptionGeneratedTask } from '../utils/helpers'
import { PAYMENT_STATUS_LABELS } from '../utils/constants'

const DEPTH_STYLES = {
  1: { label: 'Shallow', style: { color: '#4338ca', backgroundColor: '#eef2ff', borderColor: '#c7d2fe' } },
  2: { label: 'Medium', style: { color: '#7c3aed', backgroundColor: '#f3e8ff', borderColor: '#e9d5ff' } },
  3: { label: 'Deep', style: { color: '#0369a1', backgroundColor: '#e0f2fe', borderColor: '#bae6fd' } },
  4: { label: 'Very Deep', style: { color: '#0f766e', backgroundColor: '#d1fae5', borderColor: '#a7f3d0' } },
  5: { label: 'Maximum', style: { color: '#92400e', backgroundColor: '#fef3c7', borderColor: '#fde68a' } },
  default: { label: 'Depth', style: { color: '#1f2937', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' } },
}

const ANALYST_TAG_STYLE = { color: '#0f172a', backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }

const DECISION_STYLES = {
  BUY: { color: '#047857', backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' },
  SELL: { color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' },
  HOLD: { color: '#92400e', backgroundColor: '#fff7ed', borderColor: '#fed7aa' },
  DEFAULT: { color: '#475569', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' },
}

const STATUS_STYLES = {
  COMPLETED: { color: '#0f766e', backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' },
  RUNNING: { color: '#3730a3', backgroundColor: '#eef2ff', borderColor: '#c7d2fe' },
  PENDING: { color: '#92400e', backgroundColor: '#fff7ed', borderColor: '#fed7aa' },
  FAILED: { color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' },
  CANCELLED: { color: '#6b7280', backgroundColor: '#f3f4f6', borderColor: '#e5e7eb' },
  DEFAULT: { color: '#475569', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' },
}

const PAYMENT_STYLES = {
  PAID: { color: '#1d4ed8', backgroundColor: '#eff6ff', borderColor: '#bfdbfe' },
  AWAITING_PAYMENT: { color: '#c2410c', backgroundColor: '#fff7ed', borderColor: '#fed7aa' },
  PAYMENT_FAILED: { color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' },
  PAYMENT_EXPIRED: { color: '#6b7280', backgroundColor: '#f5f5f5', borderColor: '#e5e7eb' },
  FREE: { color: '#0f766e', backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' },
  DEFAULT: { color: '#475569', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' },
}
import './TaskHistory.css'
import '../styles/mobile-record.css'

const { RangePicker } = DatePicker
const { Option } = Select
const { Text } = Typography
const { useBreakpoint } = Grid

const TaskHistory = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { tasks, loading, pagination } = useSelector((state) => state.task)
  const [form] = Form.useForm()
  const [searchParams] = useSearchParams()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const defaultQueryParams = useMemo(() => ({
    page: 0,
    pageSize: 12,
    status: undefined,
    ticker: undefined,
    taskId: undefined,
    startDate: undefined,
    endDate: undefined,
    searchKeyword: undefined,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  }), [])
  const [queryParams, setQueryParams] = useState(defaultQueryParams)
  const [filtersOpen, setFiltersOpen] = useState(!isMobile)
  const runQuery = useCallback((params) => {
    dispatch(queryTasks(params))
  }, [dispatch])

  // Initial load
useEffect(() => {
  dispatch(resetTaskList())

  // Check if there's a status in the URL
  const statusFromUrl = searchParams.get('status')

  if (statusFromUrl) {
    // Set form value
    form.setFieldsValue({
      status: statusFromUrl.toLowerCase()
    })

    // Run query with status filter
    const paramsWithStatus = {
      ...defaultQueryParams,
      status: statusFromUrl.toUpperCase()
    }
    setQueryParams(paramsWithStatus)
    runQuery(paramsWithStatus)
  } else {
    // Run default query
    runQuery(defaultQueryParams)
  }
}, [dispatch, runQuery, defaultQueryParams, searchParams, form])

  useEffect(() => {
    setFiltersOpen(!isMobile)
  }, [isMobile])

  // Handle search form submission
  const handleSearch = () => {
    const values = form.getFieldsValue()

    // Convert date range to strings
    let startDate = undefined
    let endDate = undefined
    if (values.dateRange && values.dateRange.length === 2) {
      startDate = values.dateRange[0].format('YYYY-MM-DD')
      endDate = values.dateRange[1].format('YYYY-MM-DD')
    }

    const newQueryParams = {
      ...queryParams,
      page: 0, // Reset to first page on new search
      status: values.status === 'all' ? undefined : values.status?.toUpperCase(),
      startDate: startDate,
      endDate: endDate,
      searchKeyword: values.searchKeyword || undefined,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    }

    setQueryParams(newQueryParams)
    runQuery(newQueryParams)
    if (isMobile) {
      setFiltersOpen(false)
    }
  }

  // Handle reset filters
  const handleReset = () => {
    form.resetFields()
    const defaultParams = { ...defaultQueryParams }
    setQueryParams(defaultParams)
    runQuery(defaultParams)
    if (isMobile) {
      setFiltersOpen(false)
    }
  }

  // Handle pagination change
  const handlePageChange = (page, pageSize) => {
    const newQueryParams = {
      ...queryParams,
      page: page - 1, // Ant Design uses 1-based, backend uses 0-based
      pageSize: pageSize,
    }
    setQueryParams(newQueryParams)
    runQuery(newQueryParams)
  }

  const tasksToRender = tasks || []
  const currentPage = (pagination?.currentPage ?? 0) + 1
  const pageSize = pagination?.pageSize ?? queryParams.pageSize
  const totalItems = pagination?.totalElements ?? tasksToRender.length
  const hasTasks = tasksToRender.length > 0

  const handleViewTask = useCallback((task) => {
    const taskId = task.taskId || task.task_id || task.id
    if (!taskId) return
    navigate(`/tasks/${taskId}`)
  }, [navigate])

  const formatBillingAmount = useCallback((amount, currency = 'USD') => {
    if (amount === null || amount === undefined) {
      return null
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
      }).format(amount)
    } catch (error) {
      console.warn('Failed to format billing amount', error)
      return `${amount} ${currency}`
    }
  }, [])

  const renderSoftBadge = useCallback((label, palette) => (
    <span className="task-history-soft-tag" style={palette}>
      {label}
    </span>
  ), [])

  const desktopColumns = useMemo(() => {
    const columns = [
      {
        title: 'Stock',
        key: 'stock',
        align: 'left',
        render: (_, task) => {
          const analysisDate = task.analysisDate || task.analysis_date
        return (
          <Space direction="vertical" size={0} className="task-history-stock">
            <Text className="task-history-stock__ticker">
              {task.ticker || task.symbol || '-'}
            </Text>
            <Text type="secondary" className="task-history-stock__date">
              {analysisDate ? formatDate(analysisDate) : '—'}
            </Text>
          </Space>
        )
      },
        width: 180,
      },
    {
      title: 'Depth',
      key: 'depth',
      align: 'center',
      render: (_, task) => {
        const depthLevel = Number(task.researchDepth ?? task.research_depth ?? 1)
        const depthStyle = DEPTH_STYLES[depthLevel] || DEPTH_STYLES.default
        return renderSoftBadge(depthStyle.label, depthStyle.style)
      },
      width: 150,
    },
    {
      title: 'Analysts',
      key: 'analysts',
      align: 'center',
      render: (_, task) => {
        const selected = task.selectedAnalysts || task.selected_analysts
        const analystNames = Array.isArray(selected) ? selected.filter(Boolean) : []
        const fallbackCount = Number(task.analystCount ?? task.analyst_count ?? 0)
        const analystCount = analystNames.length || fallbackCount

        if (!analystCount) {
          return <Text type="secondary">-</Text>
        }

        const label = `${analystCount} ${analystCount === 1 ? 'Analyst' : 'Analysts'}`
        const content = renderSoftBadge(label, ANALYST_TAG_STYLE)

        return analystNames.length > 0 ? (
          <Tooltip title={analystNames.join(', ')}>
            {content}
          </Tooltip>
        ) : content
      },
        width: 160,
      },
      {
        title: 'Decision',
        key: 'decision',
        align: 'center',
        render: (_, task) => {
          const decision = task.finalDecision || task.final_decision || task.decision
          if (!decision) {
            return <Text type="secondary">-</Text>
          }
          const decisionKey = decision.toUpperCase()
          const palette = DECISION_STYLES[decisionKey] || DECISION_STYLES.DEFAULT
          return (
            <Text strong style={{ color: palette.color }}>
              {decisionKey}
            </Text>
          )
        },
        width: 150,
      },
      {
        title: 'Status',
        key: 'status',
        align: 'right',
        render: (_, task) => {
          const status = (task.status || '').toUpperCase()
          const palette = STATUS_STYLES[status] || STATUS_STYLES.DEFAULT
          const displayStatus = status
            ? status.charAt(0) + status.slice(1).toLowerCase()
            : ''
          return (
            <Text strong style={{ color: palette.color }}>
              {status ? displayStatus : '-'}
            </Text>
          )
        },
        width: 140,
      },
      {
        title: 'Payment',
        key: 'paymentStatus',
        align: 'right',
        render: (_, task) => {
          const paymentStatus = (task.paymentStatus || task.payment_status || '').toUpperCase()
          if (!paymentStatus) {
            return <Text type="secondary">-</Text>
          }
          const palette = PAYMENT_STYLES[paymentStatus] || PAYMENT_STYLES.DEFAULT
          const label = PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus
          return (
            <Text strong style={{ color: palette.color }}>
              {label}
            </Text>
          )
        },
        width: 190,
      },
      {
        title: 'Billing',
        key: 'billing',
        align: 'right',
      render: (_, task) => {
        if (isSubscriptionGeneratedTask(task)) {
          return <Text strong>Subscription</Text>
        }
        const amount = task.billingAmount ?? task.billing_amount
        const currency = task.billingCurrency || task.billing_currency || 'USD'
        const formatted = formatBillingAmount(amount, currency)
        return formatted ? (
          <Text strong>{formatted}</Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      },
        width: 140,
      },
      {
        title: 'Created',
        key: 'createdAt',
        align: 'right',
        render: (_, task) => {
          const createdAt = task.createdAt || task.created_at
          return (
            <Text type="secondary">
              {createdAt ? formatDateTime(createdAt) : '-'}
            </Text>
          )
        },
        width: 200,
      },
    ]

    const getHeaderClass = (alignValue) => {
      if (alignValue === 'right') return 'task-history-header-right'
      if (alignValue === 'center') return 'task-history-header-center'
      return 'task-history-header-left'
    }

    return columns.map((column) => {
      const alignValue = column.align || 'left'
      return {
        ...column,
        onHeaderCell: () => ({ className: getHeaderClass(alignValue) }),
      }
    })
  }, [formatBillingAmount, renderSoftBadge])

  // Handle new analysis
  const handleNewAnalysis = () => {
    navigate('/tasks/new')
  }

  return (
    <div className={`task-history-page ${isMobile ? 'mobile-record-page' : ''}`}>
      {isMobile && (
        <div className="task-history-filter-toggle mobile-record-toolbar">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="task-history-toggle-button mobile-record-toggle-btn"
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>
      )}

      {(!isMobile || filtersOpen) && (
        <Card
          bordered={false}
          className="task-history-filter-card mobile-record-filter-card"
          styles={{ body: { padding: isMobile ? 16 : 24 } }}
        >
          {isMobile && (
            <div className="task-history-filter-mobile-header">
              <Text className="task-history-filter-mobile-title">Filters</Text>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setFiltersOpen(false)}
                className="task-history-filter-close"
              />
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            className="task-history-filter-form mobile-record-filter-form"
            onFinish={handleSearch}
            initialValues={{
              status: 'all',
            }}
          >
            <div className="task-history-filter-grid mobile-record-filter-grid">
              <Form.Item name="searchKeyword" label={null} colon={false}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Ticker, task ID, or decision"
                  allowClear
                  size="large"
                />
              </Form.Item>

              <Form.Item name="status" label={null} colon={false}>
                <Select placeholder="Select status" size="large" allowClear>
                  <Option value="all">All status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="running">Running</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="failed">Failed</Option>
                </Select>
              </Form.Item>

              <Form.Item name="dateRange" label={null} colon={false}>
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  placeholder={['Start', 'End']}
                />
              </Form.Item>

              <div className="task-history-filter-actions mobile-record-filter-actions">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  className="task-history-search-btn mobile-record-search-btn"
                >
                  Search
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  size="large"
                  className="task-history-reset-btn mobile-record-reset-btn"
                >
                  Reset
                </Button>
              </div>
            </div>
          </Form>
        </Card>
      )}

      {/* Task Records */}
      {!hasTasks ? (
        <Card className="task-history-empty-card">
          {loading ? (
            <div className="task-history-empty-loading">
              <Spin size="large" />
            </div>
          ) : (
            <Empty
              description={
                <Space direction="vertical" size={12}>
                  <span>No tasks found</span>
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={handleNewAnalysis}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    Start Your First Analysis
                  </Button>
                </Space>
              }
            />
          )}
        </Card>
      ) : (
        <>
          {!isMobile ? (
            <>
              <Card
                bordered={false}
                className="task-history-table-card"
                styles={{ body: { padding: 0 } }}
              >
                <Table
                  columns={desktopColumns}
                  dataSource={tasksToRender}
                  loading={loading}
                  pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['12', '24', '48', '96'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
                  }}
                  onChange={(tablePagination) => {
                    handlePageChange(tablePagination.current, tablePagination.pageSize)
                  }}
                  rowKey={(task) => task.taskId || task.task_id || task.id}
                  scroll={{ x: true }}
                  onRow={(task) => ({
                    onClick: () => handleViewTask(task),
                    style: { cursor: (task.taskId || task.task_id || task.id) ? 'pointer' : 'default' },
                  })}
                  size="middle"
                />
              </Card>

            </>
          ) : (
            <>
              <Spin spinning={loading}>
                <div className="mobile-record-cards">
                  {tasksToRender.map((task) => (
                    <TaskCard
                      task={task}
                      key={task.taskId || task.task_id || task.id}
                    />
                  ))}
                </div>
              </Spin>

              {totalItems > 0 && (
                <Pagination
                  className="mobile-record-pagination"
                  current={currentPage}
                  total={totalItems}
                  pageSize={pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} tasks`}
                  pageSizeOptions={['12', '24', '48', '96']}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                  size="small"
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default TaskHistory
